import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import {
	slowQueryGuard,
	SLOW_QUERY_DEFAULT_TIMEOUT,
} from '@confluence/query-preloader-tools/entry-points/slowQueryGuard.ts';
import {
	useViewPdfDataQuery,
	type useViewPdfDataQuery$data,
	type useViewPdfDataQuery$variables,
} from '@confluence/viewpdf-macro/entry-points/useViewPdfDataQuery.ts';
import {
	buildViewPdfQueryVariables,
	parseViewPdfMacroParams,
	type ViewPdfMacroParams,
	type ParsedViewPdfMacroParams,
} from '@confluence/viewpdf-macro/entry-points/viewPdfMacroParamsUtils.ts';

import type {
	MacroContentNode,
	MacroPreloadHandler,
	MacroPreloadContext,
} from '../preloadMacroData.ts';

const buildVariables = (
	contentId: string,
	parsed: ParsedViewPdfMacroParams,
): useViewPdfDataQuery$variables => {
	return buildViewPdfQueryVariables({
		contentId,
		pageName: parsed.pageName,
		spaceKey: parsed.spaceKey,
		date: parsed.date,
	});
};

/**
 * Preload handler for the viewpdf macro.
 *
 * For a viewpdf extension node found in the ADF content, this handler
 * builds query variables from the macro parameters and fires the
 * useViewPdfDataQuery GraphQL query to populate the Relay store.
 */
export const viewPdfPreloadHandler: MacroPreloadHandler = {
	extensionKey: 'viewpdf',

	routes: ['view', 'space-overview', 'edit'],

	preload: async (node: MacroContentNode, context: MacroPreloadContext): Promise<void> => {
		const macroParams = node.attrs?.parameters?.macroParams as ViewPdfMacroParams | undefined;
		const parsed = parseViewPdfMacroParams(macroParams);
		if (!parsed.attachmentName) {
			return;
		}

		const variables = buildVariables(context.contentId, parsed);
		await slowQueryGuard(
			'viewPdfPreload',
			() =>
				query<useViewPdfDataQuery$data, useViewPdfDataQuery$variables>({
					query: useViewPdfDataQuery,
					variables,
					context: { single: true },
				}),
			SLOW_QUERY_DEFAULT_TIMEOUT,
		);
	},
};
